import { registrarFalla, registrarExito } from "./fallasEscritura.js";
import { useCallback, useEffect, useRef, useState } from "react";

import { borrar, guardar, leer } from "./almacen.js";

/**
 * Persistencia real de la aplicación.
 *
 * El prototipo guardaba contra `window.storage`, que es la API de artifacts de
 * Claude.ai. Fuera de ese entorno no existe: cada F5 borraba flota, pilotos,
 * bitácora y mantenimiento. Para una app cuyo propósito es dejar registro de
 * cada vuelo ejecutado, eso la invalidaba por completo.
 *
 * Acá se resuelve de dos formas:
 *   1. `usePersistedState`, que se usa igual que useState pero escribe en
 *      IndexedDB. Es lo que se usa para el estado nuevo.
 *   2. `instalarShimStorage()`, que crea un `window.storage` compatible con el
 *      que el prototipo ya llamaba, apoyado en IndexedDB. Así el código legacy
 *      empieza a persistir sin tocarlo línea por línea.
 */

/* -------------------------------------------------------------------------- */
/* Hook                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Igual que useState, pero el valor sobrevive al refresco y al cierre del
 * navegador.
 *
 * @param {string} clave     identificador estable, p. ej. "flota"
 * @param {*} inicial        valor por defecto la primera vez
 * @returns {[valor, setValor, {cargado:boolean, error:Error|null}]}
 */
/**
 * Estado persistido, con posibilidad de FUSIONAR lo guardado con lo nuevo.
 *
 * El comportamiento por defecto —lo guardado reemplaza al inicial— es correcto
 * para datos del usuario: su flota, sus vuelos, sus preferencias. Pero es
 * equivocado para CATÁLOGOS que la aplicación trae de fábrica.
 *
 * Pasó con los tipos de aeronave: al agregar el BK117 B-2, un operador que ya
 * había usado la app tenía `perfiles` guardado con un solo tipo, y el nuevo
 * nunca aparecía. La matrícula decía «BK117» y el encabezado seguía diciendo
 * «Airbus AS350 B3», porque el perfil no existía y caía al primero.
 *
 * Con `fusionar` se combinan: se conservan las ediciones del usuario y se
 * incorporan las entradas nuevas de fábrica.
 *
 * @param {(guardado, inicial) => any} [fusionar]
 */
/* ═══ UNA ESCRITURA POR CLAVE A LA VEZ ═══

   Dos `setValor` seguidos disparaban dos escrituras en paralelo, y la que
   terminaba última ganaba. Si la lenta llevaba el valor VIEJO, el nuevo se
   perdía sin error: la escritura «tuvo éxito», solo que guardó lo anterior.

   Se encolan por clave. Claves distintas siguen en paralelo: serializarlo todo
   haría que guardar la flota demore la bitácora sin motivo. */
const colas = new Map();

function encolarEscritura(clave, tarea) {
  const previa = colas.get(clave) || Promise.resolve();
  /* El `catch` vacío es para encadenar: si la anterior falló, la siguiente
     tiene que correr igual. Su error ya se publicó por su propia vía. */
  const siguiente = previa.catch(() => {}).then(tarea);
  colas.set(clave, siguiente);

  /* La cola no crece: cuando esta era la última, se suelta. */
  siguiente.catch(() => {}).finally(() => {
    if (colas.get(clave) === siguiente) colas.delete(clave);
  });

  return siguiente;
}

/* ═══ LEER DESPUÉS DE LO QUE YA SE PIDIÓ GUARDAR ═══

   El motor de sincronización lee el mismo almacén que la pantalla escribe. Si
   leyera directo, una escritura en vuelo todavía no confirmada le devolvería el
   valor ANTERIOR, y encolaría como cambio lo que ya no está. Esta espera es la
   misma cola por clave que usan los `setValor`. */

export function guardarEnCola(clave, valor) {
  return encolarEscritura(clave, () => guardar(clave, valor));
}

export async function leerTrasEscrituras(clave) {
  const pendiente = colas.get(clave);
  if (pendiente) await pendiente.catch(() => {});
  return leer(clave);
}

/* ═══ QUIÉN ESCUCHA LOS CAMBIOS LOCALES ═══

   La pantalla no importa el motor, y el motor no importa la pantalla: sería un
   ciclo entre `persistencia` y `servicio`. Los cambios se publican acá y el
   servicio de sincronización se suscribe al arrancar. */

const observadores = new Set();

export function observarCambiosDeDatos(fn) {
  if (typeof fn !== "function") return () => {};
  observadores.add(fn);
  return () => observadores.delete(fn);
}

function notificarCambio(entidad, antes, despues) {
  for (const fn of observadores) {
    try { fn({ entidad, antes, despues }); } catch { /* uno roto no frena al resto */ }
  }
}

/**
 * @param {string} [entidad] nombre de la entidad de sincronización, si esta
 *   clave se sincroniza. Sin él, el estado funciona igual que siempre.
 */
export function usePersistedState(clave, inicial, fusionar, entidad = null) {
  const [valor, setValor] = useState(inicial);
  const [cargado, setCargado] = useState(false);
  const [error, setError] = useState(null);
  const claveCompleta = `estado:${clave}`;
  const anteriorParaSync = useRef(null);
  const vieneDelServidor = useRef(false);

  // La escritura se hace después de cargar, nunca antes: sin esta bandera el
  // valor inicial pisaría lo guardado en el primer render.
  const listoParaEscribir = useRef(false);

  useEffect(() => {
    let vivo = true;

    leer(claveCompleta)
      .then((guardado) => {
        if (!vivo) return;
        if (guardado === undefined) return;
        setValor(typeof fusionar === "function" ? fusionar(guardado, inicial) : guardado);
      })
      .catch((e) => vivo && setError(e))
      .finally(() => {
        if (!vivo) return;
        setCargado(true);
        listoParaEscribir.current = true;
      });

    return () => {
      vivo = false;
    };
  }, [claveCompleta]);

  useEffect(() => {
    if (!listoParaEscribir.current) return;

    /* ═══ LA FALLA SE PUBLICA, NO SE GUARDA EN UN ESTADO QUE NADIE LEE ═══

       Estaba `.catch(setError)`, y de las 29 llamadas del legacy **ninguna
       recoge ese error**: todas destructuran `[valor, setValor]` y descartan
       el resto.

       Resultado: si la escritura fallaba —cuota llena, modo privado, otra
       pestaña bloqueando la base— el vuelo **se seguía viendo en pantalla como
       guardado**. El piloto cerraba la aplicación, volvía, y no estaba.

       Corregir las 29 llamadas funciona hoy y falla mañana: la número 30 se
       escribe sin el error y nadie lo nota. Publicarlo desde acá cubre también
       a la que todavía no existe. */
    let cancelado = false;

    encolarEscritura(claveCompleta, () => guardar(claveCompleta, valor))
      .then(() => {
        if (cancelado) return;
        registrarExito(claveCompleta);
      })
      .catch((e) => {
        if (cancelado) return;
        setError(e);
        registrarFalla(claveCompleta, e);
      });

    return () => { cancelado = true; };
  }, [claveCompleta, valor]);

  /* ═══ LOS CAMBIOS LOCALES SE AVISAN, SIN RELEER ═══

     Cuando el motor leyera IndexedDB en este punto podría encontrar la
     escritura anterior todavía en vuelo. La diferencia se calcula con los dos
     valores que React ya tiene. `anteriorParaSync` se llena recién cuando
     terminó la carga: el valor inicial y el guardado no son una edición. */
  useEffect(() => {
    if (!entidad || !cargado) return;
    if (anteriorParaSync.current === null) {
      anteriorParaSync.current = valor;
      return;
    }
    if (vieneDelServidor.current) {
      vieneDelServidor.current = false;
      anteriorParaSync.current = valor;
      return;
    }
    const antes = anteriorParaSync.current;
    anteriorParaSync.current = valor;
    notificarCambio(entidad, antes, valor);
  }, [valor, cargado, entidad]);

  /* ═══ LO QUE BAJÓ EL SERVIDOR SE VE SIN REFRESCAR ═══

     El motor escribe en IndexedDB y avisa por evento. La pantalla vuelve a
     leer, y la bandera hace que ese cambio no se encole como si el piloto lo
     hubiera hecho: sería devolverle al servidor lo que él acaba de mandar. */
  useEffect(() => {
    if (!entidad || typeof window === "undefined") return undefined;
    const alLlegar = (evento) => {
      if (evento?.detail?.clave !== clave) return;
      leerTrasEscrituras(claveCompleta).then((guardado) => {
        if (guardado === undefined) return;
        vieneDelServidor.current = true;
        /* Una copia nueva aunque el contenido sea igual: si React ve el mismo
           objeto, no vuelve a correr el efecto y la bandera quedaría armada
           para ignorar la próxima edición de verdad. */
        setValor(Array.isArray(guardado) ? [...guardado] : guardado);
      }).catch(() => {});
    };
    window.addEventListener("helicalc:datos-remotos", alLlegar);
    return () => window.removeEventListener("helicalc:datos-remotos", alLlegar);
  }, [clave, claveCompleta, entidad]);

  const reiniciar = useCallback(async () => {
    await borrar(claveCompleta);
    setValor(inicial);
  }, [claveCompleta, inicial]);

  return [valor, setValor, { cargado, error, reiniciar }];
}

/* -------------------------------------------------------------------------- */
/* Compatibilidad con el prototipo                                             */
/* -------------------------------------------------------------------------- */

/**
 * Crea `window.storage` con la misma forma que la API de artifacts, pero
 * escribiendo en IndexedDB. Se instala una sola vez, al arrancar la app.
 *
 * Si ya existe un `window.storage` real (la app corriendo dentro de
 * Claude.ai), no se toca nada.
 */
export function instalarShimStorage() {
  if (typeof window === "undefined" || window.storage) return false;

  const clave = (k, compartido) => `storage:${compartido ? "shared" : "user"}:${k}`;

  window.storage = {
    async get(k, compartido = false) {
      const valor = await leer(clave(k, compartido));
      if (valor === undefined) throw new Error(`No existe la clave ${k}`);
      return { key: k, value: valor, shared: compartido };
    },
    async set(k, valor, compartido = false) {
      await guardar(clave(k, compartido), valor);
      return { key: k, value: valor, shared: compartido };
    },
    async delete(k, compartido = false) {
      await borrar(clave(k, compartido));
      return { key: k, deleted: true, shared: compartido };
    },
    async list() {
      // El prototipo no la usa; se deja declarada para no romper si la llama.
      return { keys: [], shared: false };
    },
  };

  return true;
}
